/* @flow */
import Entity from 'lib/Entity';

import { cloneDeep } from 'lodash';

type UrlSetType = {
  cdn: string,
  tracked?: string, // Chat message attachments do not have tracked
  raw: string,
  download: string,
};

const ERROR_URL = 'https://www.remind.com/error';
const ERROR_URLS = {
  cdn: ERROR_URL,
  raw: ERROR_URL,
  download: ERROR_URL,
};

// This is half of the max image size set in r101-files.
// https://github.com/remind101/r101-files-go/blob/523fcdf56e30e8b97606b7d99941c8c07be50b04/file_urls.go#L11-L12
export const MAX_IMAGE_PREVIEW_HEIGHT = 300;

// $FlowIssue
export default class File extends Entity {
  urlRoot(): string {
    return '/files';
  }

  parse(response: Object): Object {
    const file = cloneDeep(response);

    // GraphQL FileModel has 'url' not 'urls'
    // Fill in 'cdn' in 'files' so that 'raw' & 'download' can be populated below.
    if (file['url']) {
      file['urls'] = {
        cdn: file['url'],
      };
    }

    if (file['urls'] && file['urls']['cdn']) {
      file['urls']['raw'] = file['urls']['cdn'].split('?')[0];
      file['urls']['download'] = `${file['urls']['raw']}?dl`;
    }

    if (file['type']) {
      file['content_type'] = file['type'];
    }

    return file;
  }

  getContentType(): string {
    return this.get('content_type');
  }

  getName(): string {
    return this.get('name');
  }

  getSize(): number {
    return this.get('size');
  }

  hasUrls(): boolean {
    return !!this.get('urls');
  }

  toGraphqlType(): FileGQL {
    return {
      uuid: this.get('uuid'),
      name: this.getName(),
      size: this.getSize(),
      url: this.getUrls().cdn,
      contentType: this.getContentType(),
      previewWidth: this.get('preview_width'),
      previewHeight: this.get('preview_height'),
    };
  }

  getUrls(): UrlSetType {
    const urls = this.get('urls');
    if (!urls) {
      console.error('File getUrls() called without URLs. Recovering with ERROR_URLS.');
      return ERROR_URLS;
    }
    return urls;
  }

  getPreviewDimensions(maxDimension: number): { height?: number, width?: number } {
    const height = Math.min(this.get('preview_height'), maxDimension);
    const width = Math.min(this.get('preview_width'), maxDimension);
    if (height && width) {
      return { height, width };
    } else {
      return {};
    }
  }

  getViewers(): string[] {
    return this.get('viewers') || [];
  }
}
